import { createSelector } from '@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';
import { MarketSegment } from './models';
import { baseApi } from '@centerpiece/components';

export const market_segment = baseApi
  .enhanceEndpoints({ addTagTypes: ['MarketSegments', 'MarketSegment'] })
  .injectEndpoints({
    endpoints: builder => ({
      getMarketSegments: builder.query<MarketSegment[], { params?: object }>({
        query: ({ params }: { params?: object }) => ({
          url: 'market_segment',
          method: 'GET',
          params: params,
        }),
        providesTags: (result, error, arg) =>
          result
            ? [
                ...result.map(({ id }) => ({
                  type: 'MarketSegment' as const,
                  id,
                })),
                'MarketSegments',
              ]
            : ['MarketSegments'],
      }),
      getMarketSegment: builder.query<MarketSegment, string>({
        query: id => ({
          url: `market_segment/${id}`,
          method: 'GET',
        }),
        providesTags: (result, error, arg) => [
          { type: 'MarketSegment', id: arg },
        ],
        // configuration for an individual endpoint, overriding the api setting
        keepUnusedDataFor: 90,
      }),
      createMarketSegment: builder.mutation({
        query: request => ({
          url: 'market_segment',
          method: 'POST',
          body: request,
        }),
        invalidatesTags: ['MarketSegments'],
      }),
      updateMarketSegment: builder.mutation<MarketSegment, MarketSegment>({
        query: request => ({
          url: `market_segment/${request.id}`,
          method: 'PUT',
          body: request,
        }),
        invalidatesTags: (result, error, arg) => [
          { type: 'MarketSegment', id: arg.id },
        ],
        async onQueryStarted({ id, ...patch }, { dispatch, queryFulfilled }) {
          const patchResult = dispatch(
            market_segment.util.updateQueryData(
              'getMarketSegment',
              `${id}`,
              draft => {
                Object.assign(draft, patch);
              }
            )
          );
          try {
            await queryFulfilled;
          } catch {
            patchResult.undo();

            /**
             * Alternatively, on failure you can invalidate the corresponding cache tags
             * to trigger a re-fetch:
             * dispatch(market_segment.util.invalidateTags(['MarketSegments']))
             */
            dispatch(market_segment.util.invalidateTags(['MarketSegments']));
          }
        },
      }),
      deleteMarketSegment: builder.mutation<MarketSegment, number>({
        query: id => ({
          url: `market_segment/${id}`,
          method: 'DELETE',
        }),
        invalidatesTags: (result, error, arg) =>
          result
            ? [{ type: 'MarketSegment' as const, arg }, 'MarketSegments']
            : ['MarketSegments'],
      }),
    }),
  });

// Auto-generated hooks
export const {
  useGetMarketSegmentsQuery,
  useGetMarketSegmentQuery,
  useCreateMarketSegmentMutation,
  useUpdateMarketSegmentMutation,
  useDeleteMarketSegmentMutation,
  usePrefetch: useMarketSegmentsPrefetch,
} = market_segment;
const selectMarketSegmentsRoot =
  market_segment.endpoints.getMarketSegments.select({});
export const selectMarketSegments = createSelector(
  selectMarketSegmentsRoot,
  market_segment => market_segment.data || []
);
